import {TargetBlank} from '@wandb/weave/common/util/links';
import {Icon} from '@wandb/weave/components/Icon';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {useViewBarContext} from '../NamedWorkspaceViewBarContext';
import {parseAndTrackNamedWorkspaces, WorkspaceType} from '../utils';
import {NamedWorkspaceTooltip} from './NamedWorkspaceTooltip';

const SHARED_AUTO_SAVE_WARNING_TOOLTIP_CONTENT =
  'Changes to saved views are not auto-saved. You can manually save the view or copy it to your workspace if you wish to keep your work. Unsaved changes will be discarded at the end of this session.';
const PERSONAL_AUTO_SAVE_WARNING_TOOLTIP_CONTENT =
  "Changes you make to a teammate's workspace are only visible to you and will be discarded at the end of the session. You can copy to your workspace or create a new view if you wish to keep your work.";
const PROJECT_READ_ONLY_AUTO_SAVE_WARNING_TOOLTIP_CONTENT =
  'You may explore this workspace, but any changes you make will be discarded at the end of this session.';

const getTooltipInfo = ({
  isProjectReadOnly,
  workspaceType,
}: {
  isProjectReadOnly: boolean;
  workspaceType: WorkspaceType;
}) => {
  if (isProjectReadOnly) {
    return {
      content: PROJECT_READ_ONLY_AUTO_SAVE_WARNING_TOOLTIP_CONTENT,
      labelText: 'Changes are only visible to you.',
    };
  }

  if (workspaceType === 'personal') {
    return {
      content: PERSONAL_AUTO_SAVE_WARNING_TOOLTIP_CONTENT,
      labelText: 'Changes are only visible to you.',
    };
  }

  return {
    content: SHARED_AUTO_SAVE_WARNING_TOOLTIP_CONTENT,
    labelText: 'Changes are not auto-saved.',
  };
};

type AutoSaveWarningProps = {
  isProjectReadOnly: boolean;
};
export const AutoSaveWarning = ({isProjectReadOnly}: AutoSaveWarningProps) => {
  const {workspaceType, clearChanges, currentView, viewer} =
    useViewBarContext();

  const {content, labelText} = getTooltipInfo({
    isProjectReadOnly,
    workspaceType,
  });
  return (
    <NamedWorkspaceTooltip
      tooltipContent={content}
      tooltipWidth="20rem"
      dataTest="nw-auto-save-warning"
      contentProps={{
        side: 'bottom',
        align: 'center',
      }}>
      <div
        className={twMerge(
          'text flex h-32 items-center gap-8 px-8',
          'whitespace-nowrap rounded text-base',
          'bg-sienna-300/[0.24] text-sienna-600'
        )}>
        <Icon className="h-18 w-18 shrink-0 grow-0" name="info" />
        <p className="hidden lg:contents lg:leading-[22px]">{`${labelText} `}</p>
        {workspaceType !== 'personal' && clearChanges != null && (
          <TargetBlank
            className="hidden font-semibold hover:text-sienna-500 lg:contents"
            data-test="nw-clear-changes-link"
            onClick={() => {
              parseAndTrackNamedWorkspaces({
                action: 'Clear changes',
                location: 'view bar',
                currentView,
                viewer,
              });
              clearChanges?.();
            }}>
            Clear changes
          </TargetBlank>
        )}
      </div>
    </NamedWorkspaceTooltip>
  );
};
